import InboxFillIcon from "@/assets/icons/inbox-fill.svg?react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Trash } from "iconsax-react";
import { Check } from "lucide-react";
import {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { Accept, useDropzone } from "react-dropzone";
import { toast } from "sonner";
import styles from "./attachment-uploader.module.css";
import Spinner from "./spinner";

type AttachmentUploaderProps = {
  buttons?: ReactNode;
  afterAcceptFile?: (file: File) => void;
  afterRemoveFile?: () => void;
  isLoading?: boolean;
};

export type AttachmentUploaderRef = {
  /**
   * Clears the accepted file without any callback
   */
  clearAcceptedFile: () => void;
};

const AttachmentUploader = forwardRef<
  AttachmentUploaderRef,
  AttachmentUploaderProps
>((props, ref) => {
  const [acceptedFile, setAcceptedFile] = useState<File | null>(null);

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    const acceptedFile = acceptedFiles[0];
    setAcceptedFile(acceptedFile);
    props.afterAcceptFile?.(acceptedFile);
  }, []);

  const removeFile = () => {
    setAcceptedFile(null);
    props.afterRemoveFile?.();
  };

  useImperativeHandle(ref, () => ({
    clearAcceptedFile: () => setAcceptedFile(null),
  }));

  const acceptedMimeTypes: Accept = {
    "application/pdf": [".pdf"],
  };

  const showFileDropError = (errorCode: string, errorMessage: string) => {
    let title = "";
    let description = "";

    switch (errorCode) {
      case "file-invalid-type":
        title = "Tipo de archivo no permitido";
        description = `Solo se permiten archivos ${Object.values(acceptedMimeTypes).flat().join(", ")}`;
        break;
      case "file-too-large":
        title = "Archivo demasiado grande";
        description = `El tamaño máximo permitido es ${import.meta.env.VITE_RESOURCE_FILE_MAX_MB_SIZE}MB`;
        break;
      default:
        title = "Error al cargar archivo";
        description = errorMessage;
        break;
    }

    toast.error(title, { description });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptedMimeTypes,
    multiple: false,
    maxSize: import.meta.env.VITE_RESOURCE_FILE_MAX_MB_SIZE * 1024 * 1024,
    onDropAccepted,
    onDropRejected: ([fileRejection]) => {
      const { code, message } = fileRejection.errors[0];
      showFileDropError(code, message);
    },
  });

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "flex min-h-36 cursor-pointer flex-col items-center justify-center gap-3 rounded-lg bg-white px-8 py-6 text-center hover:bg-secondary",
          !acceptedFile && styles.wrapper,
          acceptedFile && "border",
          props.isLoading && "pointer-events-none cursor-wait bg-gray-50",
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} />

        <div
          className={cn(
            "flex h-7 w-16 items-center justify-center rounded-md bg-[#F2F4F7]",
            acceptedFile && "bg-green-300",
            props.isLoading && "bg-gray-100",
          )}
        >
          {acceptedFile ? (
            props.isLoading ? (
              <Spinner className="mr-0" />
            ) : (
              <Check className="h-5 w-5 text-green-700" />
            )
          ) : (
            <InboxFillIcon className="mx-auto h-5 w-5 text-[#8090A3]" />
          )}
        </div>

        {isDragActive ? (
          <p className="leading-snug text-[#262452]">Suelta aquí...</p>
        ) : acceptedFile ? (
          <p className="leading-snug text-[#262452]">{acceptedFile.name}</p>
        ) : (
          <p className="leading-snug text-muted-foreground">
            Haz clic para subir o arrastra tus documentos aquí
          </p>
        )}
      </div>

      <div className="flex flex-row justify-end gap-2">
        {props.buttons}

        {acceptedFile && (
          <Button
            size={"sm"}
            onClick={() => removeFile()}
            disabled={props.isLoading}
          >
            <Trash />
          </Button>
        )}
      </div>
    </div>
  );
});

AttachmentUploader.displayName = "AttachmentUploader";

export default AttachmentUploader;
